import { useEffect, useMemo, useState } from "react";
import { Bar, Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Tooltip,
  Legend,
} from "chart.js";
import * as api from "../api/client";
import { useThemeTick } from "./ChartWidget";

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Tooltip, Legend);

const PRICE_CHANGE = "price_change";
const PRICE_TREND = "price_trend";
const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
const ISO_DAY = /\b((?:19|20)\d{2})-(\d{2})-(\d{2})\b/g;
const YEAR = /\b(?:19|20)\d{2}\b/g;

const pad = (n) => String(n).padStart(2, "0");
// Local date parts, not toISOString(): that converts to UTC first, and local midnight in IST is the
// previous day in UTC, so every period would start a day early.
const isoDay = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parseDay = (s) => {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
};
const fmtDay = (d) => d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });

function fmtRange(from, to) {
  const tail = to.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  const head = from.getFullYear() === to.getFullYear()
    ? from.toLocaleDateString("en-IN", { day: "numeric", month: "short" })
    : fmtDay(from);
  return `${head} – ${tail}`;
}

/** [start, end] of one axis label, in each shape aggregate's buckets or a model's own labels take:
 *  2026, 2026Q1, 2026-03, 2026-03-14, "Mar 2026", "Mar-2026". */
function labelSpan(label) {
  const s = String(label).trim();
  let m;
  if ((m = s.match(/^(\d{4})$/))) return [new Date(+m[1], 0, 1), new Date(+m[1], 11, 31)];
  if ((m = s.match(/^(\d{4})Q([1-4])$/))) return [new Date(+m[1], (m[2] - 1) * 3, 1), new Date(+m[1], m[2] * 3, 0)];
  if ((m = s.match(/^(\d{4})-(\d{1,2})$/))) return [new Date(+m[1], m[2] - 1, 1), new Date(+m[1], +m[2], 0)];
  if ((m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/))) {
    const d = new Date(+m[1], m[2] - 1, +m[3]);
    return [d, d];
  }
  if ((m = s.match(/^([a-z]{3})[a-z]*[\s-]+(\d{4})$/i))) {
    const i = MONTHS.indexOf(m[1].toLowerCase());
    if (i >= 0) return [new Date(+m[2], i, 1), new Date(+m[2], i + 1, 0)];
  }
  return null;
}

/**
 * The period a card covers, so its competitors are compared over the same months.
 *
 * Read from the most precise statement the card makes: dates in its own query (a `filters` range, or
 * literals in its SQL), then its time axis, then years named in its title or series ("— 2026",
 * "2025 vs 2026"). A card that states none — a status breakdown over all time — gets the last 12
 * months, and the tab says so.
 */
export function cardPeriod(spec, today = new Date()) {
  const q = spec._query || spec._source || {};
  const days = [...`${JSON.stringify(q.args || {})} ${q.sql || ""}`.matchAll(ISO_DAY)]
    .map((m) => new Date(+m[1], m[2] - 1, +m[3]))
    .filter((d) => !isNaN(d));
  let from = null;
  let to = null;
  let basis = null;
  if (days.length) {
    from = new Date(Math.min(...days));
    // A lone date is an open bound ("since 1 Jan 2026"), which runs to today.
    to = days.length > 1 ? new Date(Math.max(...days)) : today;
    basis = "query";
  } else {
    const spans = (spec.labels || []).map(labelSpan);
    if (spans.length > 1 && spans.every(Boolean)) {
      from = new Date(Math.min(...spans.map((s) => s[0])));
      to = new Date(Math.max(...spans.map((s) => s[1])));
      basis = "axis";
    } else {
      const text = [spec.title, ...(spec.series || []).map((s) => s.name)].join(" ");
      const years = (text.match(YEAR) || []).map(Number);
      if (years.length) {
        from = new Date(Math.min(...years), 0, 1);
        to = new Date(Math.max(...years), 11, 31);
        basis = "title";
      }
    }
  }
  if (to && to > today) to = today;
  if (!from || from >= to) {
    const yearAgo = new Date(today);
    yearAgo.setFullYear(today.getFullYear() - 1);
    return { from: yearAgo, to: today, basis: "default" };
  }
  return { from, to, basis };
}

function quarterOverlaps(q, period) {
  const end = parseDay(q.end);
  const start = new Date(end.getFullYear(), end.getMonth() - 2, 1);
  return start <= period.to && end >= period.from;
}

/** The quarters worth offering for one metric: those inside the card's period that have any figure,
 *  newest first — or, when none do, every quarter that has one, so the tab shows the latest known
 *  numbers (and says they are outside the period) rather than nothing. */
function quarterChoices(sheet, key, period) {
  const filled = sheet.quarters.filter((q) => Object.keys(q.values[key] || {}).length > 0);
  const inPeriod = filled.filter((q) => quarterOverlaps(q, period));
  return { list: (inPeriod.length ? inPeriod : filled).slice().reverse(), inPeriod: inPeriod.length > 0 };
}

function formatValue(v, unit) {
  if (v === null || v === undefined) return "—";
  if (unit === "₹ Cr") {
    // Sign ahead of the symbol: a loss reads "-₹50 Cr", not "₹-50 Cr".
    const abs = Math.abs(v).toLocaleString("en-IN", { maximumFractionDigits: Math.abs(v) < 100 ? 1 : 0 });
    return `${v < 0 ? "-" : ""}₹${abs} Cr`;
  }
  if (unit === "%") return `${v > 0 ? "+" : ""}${v.toFixed(1)}%`;
  return `${v.toLocaleString("en-IN", { maximumFractionDigits: 2 })}${unit ? ` ${unit}` : ""}`;
}

// Candidate hues for rival lines, most mutually distinct first.
const LINE_HUES = ["#ef4444", "#f59e0b", "#06b6d4", "#db2777", "#65a30d", "#7c3aed", "#059669", "#ea580c",
  "#0ea5e9", "#a16207", "#0d9488", "#4f46e5"];

function hueOf(hex) {
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255);
  const max = Math.max(r, g, b);
  const d = max - Math.min(r, g, b);
  if (!d) return null;
  const h = max === r ? ((g - b) / d) % 6 : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
  return (h * 60 + 360) % 360;
}

/** Colours from the live theme, re-read on a theme change (see useThemeTick). The own company takes
 *  the theme's accent; rivals share one neutral in the bar charts so the eye lands on the own company
 *  first, and get distinct hues in the trend chart, where every line has to be told apart. Any hue
 *  close to the accent is dropped from the rivals' — the chart palette's own tail puts violet beside
 *  indigo, which made a rival's line read as the own company's. */
function readColors() {
  const cs = getComputedStyle(document.documentElement);
  const v = (name, fallback) => (cs.getPropertyValue(name) || "").trim() || fallback;
  const own = v("--chart-1", "#4f46e5");
  const ownHue = /^#[0-9a-f]{6}$/i.test(own) ? hueOf(own) : null;
  const apart = (c) => ownHue === null || Math.min(Math.abs(hueOf(c) - ownHue), 360 - Math.abs(hueOf(c) - ownHue)) > 35;
  return {
    own,
    rival: v("--slate-400", "#94a3b8"),
    text: v("--slate-600", "#475569"),
    grid: v("--slate-100", "#f1f5f9"),
    lines: LINE_HUES.filter(apart),
  };
}

/** Each bar's value written at its tip. A comparison is read for its numbers, and hovering nine bars
 *  one by one to find the own company's is the wrong default. */
const barValueLabels = {
  id: "competeValues",
  afterDatasetsDraw(chart, _args, opts) {
    const data = chart.data.datasets[0]?.data || [];
    const { ctx } = chart;
    ctx.save();
    ctx.font = "600 11px Inter, system-ui, sans-serif";
    ctx.fillStyle = opts.color;
    ctx.textBaseline = "middle";
    chart.getDatasetMeta(0).data.forEach((bar, i) => {
      const v = data[i];
      if (v === null || v === undefined) return;
      ctx.textAlign = v < 0 ? "right" : "left";
      ctx.fillText(opts.format(v), bar.x + (v < 0 ? -5 : 5), bar.y);
    });
    ctx.restore();
  },
};

function barChart(rows, unit, colors) {
  return {
    data: {
      labels: rows.map((r) => r.name),
      datasets: [{
        data: rows.map((r) => r.value),
        backgroundColor: rows.map((r) => (r.own ? colors.own : colors.rival)),
        borderRadius: 4,
        maxBarThickness: 20,
      }],
    },
    options: {
      indexAxis: "y",
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      plugins: {
        legend: { display: false },
        tooltip: { callbacks: { label: (ctx) => formatValue(ctx.raw, unit) } },
        competeValues: { format: (v) => formatValue(v, unit), color: colors.text },
      },
      scales: {
        // The headroom keeps the longest bar's value label inside the canvas.
        x: {
          beginAtZero: true,
          grace: "50%",
          grid: { color: colors.grid },
          ticks: {
            color: colors.text,
            font: { size: 10 },
            maxTicksLimit: 5,
            callback: (v) => (unit === "%" ? `${v}%` : Number(v).toLocaleString("en-IN")),
          },
        },
        y: {
          grid: { display: false },
          ticks: { color: colors.text, font: (ctx) => ({ size: 11, weight: rows[ctx.index]?.own ? "700" : "400" }) },
        },
      },
    },
  };
}

function lineChart(prices, colors) {
  const shown = prices.series.filter((s) => s.index.some((v) => v !== null));
  const first = parseDay(prices.dates[0]);
  const last = parseDay(prices.dates[prices.dates.length - 1]);
  const long = last - first > 330 * 864e5;
  let hue = 0;
  return {
    data: {
      labels: prices.dates.map((d) =>
        parseDay(d).toLocaleDateString("en-IN", long ? { month: "short", year: "2-digit" } : { day: "numeric", month: "short" })),
      datasets: shown.map((s) => {
        const color = s.own ? colors.own : colors.lines[hue++ % colors.lines.length];
        return {
          label: s.name,
          data: s.index,
          borderColor: color,
          backgroundColor: color,
          borderWidth: s.own ? 3 : 1.5,
          pointRadius: 0,
          pointHoverRadius: 3,
          tension: 0.25,
          spanGaps: true,
          order: s.own ? 0 : 1, // the lowest order is drawn last, i.e. on top of the rivals
        };
      }),
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: { position: "bottom", labels: { boxWidth: 10, boxHeight: 3, padding: 6, font: { size: 10 }, color: colors.text } },
        tooltip: {
          filter: (item) => item.raw !== null && item.raw !== undefined,
          itemSort: (a, b) => b.raw - a.raw,
          callbacks: {
            label: (ctx) => {
              const close = shown[ctx.datasetIndex].close[ctx.dataIndex];
              return `${ctx.dataset.label}: ${ctx.raw.toFixed(1)}  (₹${close.toLocaleString("en-IN")})`;
            },
          },
        },
      },
      scales: {
        x: { grid: { display: false }, ticks: { color: colors.text, font: { size: 10 }, maxTicksLimit: 6, maxRotation: 0 } },
        y: { grid: { color: colors.grid }, ticks: { color: colors.text, font: { size: 10 } } },
      },
    },
  };
}

function Empty({ title, text }) {
  return (
    <div className="compete-empty">
      {title && <strong>{title}</strong>}
      {text && <span>{text}</span>}
    </div>
  );
}

/** What a price chart needs saying underneath: bonus/split adjustments, late starters, gaps. */
function priceNotes(prices) {
  const notes = [];
  if (prices.adjusted.length) {
    notes.push(`Adjusted for a bonus/split: ${prices.adjusted.map((a) => `${a.name} (${fmtDay(parseDay(a.date))})`).join(", ")}.`);
  }
  const late = prices.series.filter((s) => s.since && s.change !== null);
  if (late.length) notes.push(`Measured from their first close in the period: ${late.map((s) => `${s.name} (${fmtDay(parseDay(s.since))})`).join(", ")}.`);
  const none = prices.series.filter((s) => s.change === null);
  if (none.length) notes.push(`No NSE prices for ${none.map((s) => `${s.name} (${s.symbol})`).join(", ")} — check the nse_symbol.`);
  if (prices.clipped) notes.push("NSE's free archive starts on 1 Jan 2024, so earlier months are left out.");
  if (prices.unavailable) notes.push(`${prices.unavailable} sample day${prices.unavailable === 1 ? "" : "s"} could not be downloaded from NSE.`);
  return notes.join(" ");
}

/**
 * The dashboard card's Competitive tab: the own company against its listed rivals, over the same
 * period as the card. Quarterly figures come from competitors.csv, which the team fills in from each
 * company's investor presentation; share prices come from NSE automatically. See app/competitors.py.
 */
export default function CompetitiveTab({ spec }) {
  const period = useMemo(() => cardPeriod(spec), [spec]);
  const fromIso = isoDay(period.from);
  const toIso = isoDay(period.to);
  const periodKey = `${fromIso}|${toIso}`;
  const tick = useThemeTick();
  // `tick` is not read inside the callback — it is here purely to re-read the palette on a theme change.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const colors = useMemo(() => readColors(), [tick]);

  const [sheet, setSheet] = useState(null);
  const [sheetError, setSheetError] = useState(null);
  const [metric, setMetric] = useState(null);
  const [quarterEnd, setQuarterEnd] = useState(null);
  // Tagged with the period it was fetched for, so a card whose period changes (a refresh) never shows
  // the previous period's prices.
  const [priceState, setPriceState] = useState({ key: null, data: null, error: null });
  const isPrice = metric === PRICE_CHANGE || metric === PRICE_TREND;
  const prices = priceState.key === periodKey ? priceState.data : null;
  const pricesError = priceState.key === periodKey ? priceState.error : null;

  useEffect(() => {
    let cancelled = false;
    api
      .getCompetitors()
      .then((d) => !cancelled && setSheet(d))
      .catch((e) => !cancelled && setSheetError(e.message));
    return () => {
      cancelled = true;
    };
  }, []);

  // Pre-sales first when the team has entered any; share price otherwise, since it needs no data entry
  // and so always has something to show.
  useEffect(() => {
    if (!sheet || metric) return;
    const entered = sheet.metrics.find((m) => quarterChoices(sheet, m.key, period).list.length);
    setMetric(entered ? entered.key : PRICE_CHANGE);
  }, [sheet, metric, period]);

  // Fetched only once a price view is picked: a cold period downloads NSE's files, and a card whose tab
  // stays on pre-sales should not pay for that.
  useEffect(() => {
    if (!isPrice || priceState.key === periodKey) return undefined;
    let cancelled = false;
    api
      .getCompetitorPrices(fromIso, toIso)
      .then((data) => !cancelled && setPriceState({ key: periodKey, data, error: null }))
      .catch((e) => !cancelled && setPriceState({ key: periodKey, data: null, error: e.message }));
    return () => {
      cancelled = true;
    };
  }, [isPrice, priceState.key, periodKey, fromIso, toIso]);

  if (sheetError) return <Empty title="Couldn't load competitor data." text={sheetError} />;
  if (!sheet || !metric) return <Empty text="Loading competitor data…" />;

  const metricDef = sheet.metrics.find((m) => m.key === metric);
  const choices = metricDef ? quarterChoices(sheet, metric, period) : { list: [], inPeriod: false };
  const quarter = choices.list.find((q) => q.end === quarterEnd) || choices.list[0] || null;

  let body;
  let foot = null;
  if (metricDef) {
    if (!quarter) {
      body = (
        <Empty
          title={`No ${metricDef.label.toLowerCase()} figures entered yet.`}
          text={`Your team adds each company's figure from its quarterly investor presentation to ${sheet.file} on the server (see the README beside it). This tab re-reads the file every time it opens.`}
        />
      );
    } else {
      const rows = sheet.companies.map((c) => ({ ...c, value: quarter.values[metric]?.[c.symbol] ?? null }));
      const shown = rows.filter((r) => r.value !== null).sort((a, b) => b.value - a.value);
      const missing = rows.filter((r) => r.value === null).map((r) => r.name);
      const sources = sheet.companies.filter((c) => quarter.sources[c.symbol]);
      body = <Bar {...barChart(shown, metricDef.unit, colors)} plugins={[barValueLabels]} />;
      foot = (
        <>
          {metricDef.label} ({metricDef.unit}), {quarter.label} · {quarter.span}, from each company's investor presentation.
          {!choices.inPeriod && " No quarter in this card's period has figures yet, so this is the latest entered."}
          {missing.length > 0 && ` Not entered yet: ${missing.join(", ")}.`}
          {sheet.warnings.length > 0 && (
            <span className="compete-warn" title={sheet.warnings.join("\n")}>
              {" "}⚠ {sheet.warnings.length} issue{sheet.warnings.length === 1 ? "" : "s"} in competitors.csv — hover to see.{" "}
            </span>
          )}
          {sources.length > 0 && (
            <details className="compete-sources">
              <summary>Sources</summary>
              {sources.map((c) => (
                <a key={c.symbol} href={quarter.sources[c.symbol]} target="_blank" rel="noopener noreferrer">
                  {c.name}
                </a>
              ))}
            </details>
          )}
        </>
      );
    }
  } else if (pricesError) {
    body = <Empty title="Couldn't get share prices." text={pricesError} />;
  } else if (!prices) {
    body = (
      <Empty text="Fetching official NSE closing prices… The first view of a new period downloads NSE's daily files, so it can take several seconds; after that it is instant." />
    );
  } else {
    const range = prices.dates.length
      ? `${fmtDay(parseDay(prices.dates[0]))} → ${fmtDay(parseDay(prices.dates[prices.dates.length - 1]))}`
      : "";
    if (metric === PRICE_CHANGE) {
      const rows = prices.series
        .filter((s) => s.change !== null)
        .map((s) => ({ ...s, value: s.change }))
        .sort((a, b) => b.value - a.value);
      body = rows.length ? (
        <Bar {...barChart(rows, "%", colors)} plugins={[barValueLabels]} />
      ) : (
        <Empty title="No closing prices for this period." text={priceNotes(prices)} />
      );
      foot = `Share price change, ${range}, from official NSE closing prices. ${priceNotes(prices)}`;
    } else {
      body = prices.dates.length > 1 ? (
        <Line {...lineChart(prices, colors)} />
      ) : (
        <Empty title="Not enough closing prices in this period for a trend." text={priceNotes(prices)} />
      );
      foot = `Each company = 100 at its first close (${range}), from official NSE closing prices. ${priceNotes(prices)}`;
    }
  }

  return (
    <div className="compete">
      <div className="compete-bar">
        <select
          className="compete-select"
          value={metric}
          aria-label="What to compare"
          onChange={(e) => {
            setMetric(e.target.value);
            setQuarterEnd(null);
          }}
        >
          <optgroup label="Quarterly · investor presentations">
            {sheet.metrics.map((m) => (
              <option key={m.key} value={m.key}>
                {m.label}
                {m.entered ? "" : " (not entered yet)"}
              </option>
            ))}
          </optgroup>
          <optgroup label="Share price · NSE, automatic">
            <option value={PRICE_CHANGE}>Share price change</option>
            <option value={PRICE_TREND}>Share price trend</option>
          </optgroup>
        </select>
        {quarter && (
          <select
            className="compete-select"
            value={quarter.end}
            aria-label="Quarter"
            onChange={(e) => setQuarterEnd(e.target.value)}
          >
            {choices.list.map((q) => (
              <option key={q.end} value={q.end}>
                {q.label} · {q.span}
              </option>
            ))}
          </select>
        )}
        <span
          className="compete-period"
          title={period.basis === "default" ? "This card has no dates, so the last 12 months are used" : "The period this card covers"}
        >
          {period.basis === "default" ? "Last 12 months" : fmtRange(period.from, period.to)}
        </span>
      </div>
      <div className="compete-chart">{body}</div>
      {foot && <div className="compete-foot">{foot}</div>}
    </div>
  );
}
